package presentation.screens.decide

import com.kinoshazam.taste.TasteCatalogItem
import com.kinoshazam.taste.TasteCompany
import com.kinoshazam.taste.TasteCompleteness
import com.kinoshazam.taste.TasteDuration
import com.kinoshazam.taste.TasteMood
import com.kinoshazam.taste.TastePartyDto
import com.kinoshazam.taste.TastePickDto
import com.kinoshazam.taste.TasteTitleRef
import com.kinoshazam.taste.isPartyCompany
import utils.UiAction
import utils.UiState

enum class DecideStep {
    DURATION,
    COMPANY,
    MOOD,
    CONSTRAINTS,
    SEEDS,
    LOBBY,
    PICK,
}

data class DecideState(
    val step: DecideStep = DecideStep.DURATION,
    val duration: TasteDuration? = null,
    val company: TasteCompany? = null,
    val mood: TasteMood? = null,
    val excludeTags: Set<String> = emptySet(),
    val noSubtitles: Boolean = false,
    val excludeWatched: Boolean = true,
    val highRating: Boolean = false,
    val selectedSeeds: List<TasteTitleRef> = emptyList(),
    val candidates: List<TasteCatalogItem> = emptyList(),
    val seedPage: Int = 1,
    val isLoadingSeeds: Boolean = false,
    val seedsExhausted: Boolean = false,
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    val isError: Boolean = false,
    val pickLoadFailed: Boolean = false,
    val pick: TastePickDto? = null,
    val shownIds: List<TasteTitleRef> = emptyList(),
    val completeness: TasteCompleteness? = null,
    val askExitConfirm: Boolean = false,
    val party: TastePartyDto? = null,
    val joiningPartyCode: String? = null,
    val joinCodeDraft: String = "",
    val joinFailed: Boolean = false,
    val hallNotFound: Boolean = false,
    val linkCopied: Boolean = false,
) : UiState {
    val canContinueSeeds: Boolean
        get() = selectedSeeds.size >= MIN_SEEDS

    val isPartyFlow: Boolean
        get() = party != null || joiningPartyCode != null || company?.isPartyCompany() == true

    companion object {
        const val MIN_SEEDS = 3
        const val MAX_SEEDS = 20
    }
}

sealed interface DecideMVIAction : UiAction {
    data object Back : DecideMVIAction
    data object Next : DecideMVIAction
    data object Reload : DecideMVIAction
    data object LoadMoreSeeds : DecideMVIAction
    data object EditSeeds : DecideMVIAction
    data class SelectDuration(val value: TasteDuration) : DecideMVIAction
    data class SelectCompany(val value: TasteCompany) : DecideMVIAction
    data class SelectMood(val value: TasteMood) : DecideMVIAction
    data class ToggleTag(val tag: String) : DecideMVIAction
    data object ToggleNoSubtitles : DecideMVIAction
    data object ToggleExcludeWatched : DecideMVIAction
    data object ToggleHighRating : DecideMVIAction
    data class ToggleSeed(val item: TasteCatalogItem) : DecideMVIAction
    data object WatchPick : DecideMVIAction
    data object AnotherPick : DecideMVIAction
    data object NotForMe : DecideMVIAction
    data object AlreadyWatched : DecideMVIAction
    data object RequestExit : DecideMVIAction
    data object ConfirmExit : DecideMVIAction
    data object DismissExit : DecideMVIAction
    data object CopyInviteLink : DecideMVIAction
    data object HostPickNow : DecideMVIAction
    data object LockPick : DecideMVIAction
    data class SetPartyCapacity(val capacity: Int) : DecideMVIAction
    data class UpdateJoinCode(val value: String) : DecideMVIAction
    data object SubmitJoinCode : DecideMVIAction
    data object StartOwnPick : DecideMVIAction
}
